import { StorefrontModule } from '@vue-storefront/core/lib/modules';
import { isServer } from '@vue-storefront/core/helpers'
import { ANALYTICAL_CONSENT, whenConsented } from 'src/modules/gdpr-cookie-setup/helpers'

// Extend Window interface to include Microsoft Clarity globals
declare global {
  interface Window {
    clarity?: any,
    clarityInsightsLoaded?: boolean
  }
}

export const KEY = 'microsoftClarity'

const microsoftClarity = {
  namespaced: true
}

/**
 * Get Clarity project ID from config with fallback
 */
const getClarityProjectId = (appConfig): string => {
  return appConfig.microsoftClarity?.projectId || 'ofuz22s8m4'
}

/**
 * Check if Clarity is enabled in config (default: true)
 */
const isClarityEnabled = (appConfig): boolean => {
  return appConfig.microsoftClarity?.enabled !== false
}

/**
 * Check if Microsoft Clarity is already loaded
 */
const isClarityAlreadyLoaded = (): boolean => {
  return !!(window.clarity || window.clarityInsightsLoaded ||
           document.querySelector('script[src*="clarity.ms"]'))
}

/**
 * Load Microsoft Clarity script with basic error handling
 */
const loadClarityScript = (projectId: string): void => {
  try {
    // Prevent duplicate loading
    if (isClarityAlreadyLoaded()) {
      return
    }

    // Set flag to prevent duplicate attempts
    window.clarityInsightsLoaded = true

    const script = document.createElement('script')
    script.type = 'text/javascript'
    script.async = true

    script.text = `
      (function(c,l,a,r,i,t,y){
        try {
          // Prevent duplicate initialization
          if (c.clarity) return;

          c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
          t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
          y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);

        } catch (err) {
          console.error('[microsoft-clarity] Initialization error:', err);
        }
      })(window, document, "clarity", "script", "${projectId}");
    `

    script.onerror = () => {
      window.clarityInsightsLoaded = false
      console.error('[microsoft-clarity] Failed to load script')
    }

    const head = document.getElementsByTagName('head')[0]
    if (head) {
      head.appendChild(script)
    }
  } catch (error) {
    window.clarityInsightsLoaded = false
    console.error('[microsoft-clarity] Critical error:', error)
  }
}

/**
 * @param {Object} context
 * @return {void}
 */
export const MicrosoftClarityModule: StorefrontModule = function ({ store, appConfig }) {
  store.registerModule(KEY, microsoftClarity)

  if (isServer || !isClarityEnabled(appConfig)) {
    return
  }

  whenConsented(ANALYTICAL_CONSENT, () => {
    try {
      loadClarityScript(getClarityProjectId(appConfig))
    } catch (error) {
      console.error('[microsoft-clarity] Module initialization error:', error)
    }
  })
}
